/**
 * DissatisfactionRatioCard Component
 *
 * Displays the dissatisfaction ratio as a donut chart matching the
 * InsightCard style from the records page.
 *
 * Atomic level: Molecule
 * Composes: DonutChart (from records), DeltaIndicator
 * Used by: MetricStrip
 */

import React from 'react';
import { Card } from '../../../ui/card';
import { DonutChart } from '../../records/components/DonutChart';
import { DeltaIndicator } from './DeltaIndicator';
import type { TrendDirection } from '../types/today.types';

// Dissatisfaction color (amber) - matches records page
const DISSATISFACTION_COLOR = '#F59E0B';

export interface DissatisfactionRatioCardProps {
  value: number;
  delta: number;
  trend: TrendDirection;
  noData?: boolean;
}

/**
 * Dissatisfaction Ratio card with donut chart matching records page style.
 */
export function DissatisfactionRatioCard({
  value,
  delta,
  trend: _trend,
  noData = false,
}: DissatisfactionRatioCardProps): React.ReactElement {
  return (
    <Card className="gap-0 overflow-hidden rounded-2xl border-border/70 bg-card/90 p-0 shadow-[var(--shadow-card)]" padding="sm">
      <div className="space-y-4 p-5">
        <h3 className="font-jakarta text-[15px] font-bold tracking-[-0.02em] text-foreground">
          Dissatisfaction Ratio
        </h3>

        <div className="flex justify-center">
          {noData ? (
            <div className="flex h-28 w-28 items-center justify-center rounded-full border border-border/70 bg-muted/40">
              <span className="text-2xl font-semibold text-muted-foreground">N/A</span>
            </div>
          ) : (
            <DonutChart
              percentage={value}
              color={DISSATISFACTION_COLOR}
              size={112}
              strokeWidth={10}
            />
          )}
        </div>

        <div className="flex justify-center">
          <DeltaIndicator delta={delta} showValue={true} size="md" />
        </div>
      </div>
    </Card>
  );
}
